import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // Carte de résumé des avis (assets/th-etoiles.js) ouverte dans le tiroir : Échap ne ferme qu'elle,
    // et son lien « Lire les avis » referme le tiroir avant de faire défiler la page jusqu'aux avis.
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onClicAvis, true);
    // Menu enrichi : l'événement « toggle » ne remonte pas, on l'écoute en phase de capture
    this.addEventListener('toggle', this.#onAccordionToggle, true);
    this.#setupAnimatedElementListeners();
    // Liens légaux et réseaux repris du pied de page DÈS LE CHARGEMENT (le module est différé : le
    // pied de page est déjà dans la page) — jamais à l'ouverture, où ils apparaissaient après coup.
    this.#syncBas();
  }

  #basSynchro = false;

  #syncBas() {
    if (this.#basSynchro) return;
    this.#basSynchro = true;
    try {
      this.#syncLegal();
    } catch (e) {
      /* les liens rendus par Liquid restent affichés */
    }
    try {
      this.#syncReseaux();
    } catch (e) {
      /* sans réseaux repris, la rangée reste masquée */
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onClicAvis, true);
    this.removeEventListener('toggle', this.#onAccordionToggle, true);
    document.documentElement.classList.remove('menu-tiroir-ouvert');
  }

  /**
   * Liens légaux du bas du tiroir : reprend les liens EXACTS du pied de page (bloc « Liens vers
   * les politiques » : politiques + liens libres, ex. rétractation). La liste rendue par Liquid
   * (politiques de la boutique) sert de repli quand le pied de page n'a pas ce bloc.
   */
  #syncLegal() {
    const zone = this.querySelector('[data-mdx-legal]');
    const liste = zone ? zone.querySelector('[data-mdx-legal-liste]') : null;
    if (!zone || !liste) return;

    /** @type {{href: string, texte: string}[]} */
    const liens = [];
    /** @type {Record<string, boolean>} */
    const vus = {};
    document.querySelectorAll('.policy_list a[href]').forEach((a) => {
      if (this.contains(a)) return;
      const href = a.getAttribute('href') || '';
      const texte = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (!href || !texte || vus[href]) return;
      vus[href] = true;
      liens.push({ href, texte });
    });
    if (!liens.length) return;

    const modele = liste.querySelector('li');
    const classeLien = 'mdx-legal__lien';
    const fragment = document.createDocumentFragment();
    liens.forEach(({ href, texte }) => {
      /** @type {HTMLElement} */
      let li;
      if (modele) {
        li = /** @type {HTMLElement} */ (modele.cloneNode(true));
      } else {
        li = document.createElement('li');
        li.className = 'mdx-legal__item';
        const nouveau = document.createElement('a');
        nouveau.className = classeLien;
        li.appendChild(nouveau);
      }
      const a = li.querySelector('a');
      if (!a) return;
      a.setAttribute('href', href);
      a.removeAttribute('aria-current');
      let span = a.querySelector('span');
      if (!span) {
        span = document.createElement('span');
        a.appendChild(span);
      }
      span.textContent = texte;
      fragment.appendChild(li);
    });
    while (liste.firstChild) liste.removeChild(liste.firstChild);
    liste.appendChild(fragment);
    zone.hidden = false;
  }

  /**
   * Réseaux sociaux du bas du tiroir : repris des liens de réseaux sociaux du pied de page
   * (blocs « Lien de réseau social »). Rien à ressaisir ; sans lien, la rangée reste masquée.
   */
  #syncReseaux() {
    const liste = this.querySelector('[data-mdx-reseaux]');
    if (!liste) return;

    /** @type {Record<string, boolean>} */
    const vus = {};
    const fragment = document.createDocumentFragment();
    let nb = 0;
    document.querySelectorAll('.social-icons__icon-wrapper a[href]').forEach((a) => {
      if (this.contains(a) || a.getAttribute('aria-disabled') === 'true') return;
      const href = a.getAttribute('href') || '';
      if (!href || href.indexOf('//') < 0 || vus[href]) return;
      vus[href] = true;
      const svg = a.querySelector('svg');
      const li = document.createElement('li');
      const lien = document.createElement('a');
      lien.className = 'mdx-reseaux__lien';
      lien.setAttribute('href', href);
      lien.setAttribute('target', '_blank');
      lien.setAttribute('rel', 'noopener noreferrer');
      lien.setAttribute('aria-label', a.getAttribute('aria-label') || '');
      if (svg) lien.appendChild(svg.cloneNode(true));
      li.appendChild(lien);
      fragment.appendChild(li);
      nb++;
    });
    if (!nb) return;
    while (liste.firstChild) liste.removeChild(liste.firstChild);
    liste.appendChild(fragment);
    liste.hidden = false;
  }

  /**
   * Menu enrichi : tient aria-expanded à jour sur les accordéons (details.mdx-acc)
   * et, si demandé, n'en laisse qu'un seul ouvert à la fois.
   * @param {Event} event
   */
  #onAccordionToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('mdx-acc')) return;

    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (details.open && details.closest('[data-mdx-un-seul]')) {
      this.querySelectorAll('details.mdx-acc[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }
  };

  /** Échap pressé alors qu'une carte de résumé des avis est ouverte dans le tiroir */
  #echapCarte = false;

  /**
   * Relevé AVANT assets/th-etoiles.js (qui écoute sur document et referme la carte au keydown) :
   * si une carte est ouverte, le keyup qui suit ne doit pas fermer le tiroir.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    this.#echapCarte = !!this.querySelector('.etres--ouvert');
  };

  /**
   * Lien « Lire les avis » de la carte de résumé : le tiroir se ferme, puis assets/th-etoiles.js
   * fait défiler la page jusqu'à la section d'avis.
   * @param {MouseEvent} event
   */
  #onClicAvis = (event) => {
    const cible = event.target instanceof Element ? event.target.closest('a[data-etres-lien]') : null;
    if (cible && this.isOpen) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#echapCarte) {
      this.#echapCarte = false;
      return;
    }

    // Menu enrichi : Échap replie l'accordéon ouvert, sinon ferme le tiroir
    // (sans ce cas, le focus dans un accordéon replié bloquait la fermeture).
    const accordion = event.target instanceof Element ? event.target.closest('details.mdx-acc') : null;
    if (accordion) {
      if (accordion.open) {
        accordion.open = false;
        accordion.querySelector(':scope > summary')?.focus();
      } else {
        this.close();
        this.refs.details.querySelector(':scope > summary')?.focus();
      }
      return;
    }

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      // Tiroir menu ouvert : la barre d'achat collante se retire (elle ne doit jamais le recouvrir)
      document.documentElement.classList.add('menu-tiroir-ouvert');
      this.#syncBas();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    if (details === this.refs.details) document.documentElement.classList.remove('menu-tiroir-ouvert');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details):not(.mdx-acc)');
          this.querySelectorAll('details.mdx-acc[open]').forEach((accordion) => {
            accordion.open = false;
          });
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
